import { useCallback, useMemo } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Card, CardContent, Chip, Grid, Stack, Typography, useTheme, Button } from '@mui/material'
import { DataGrid, GridColDef, GridToolbarContainer, useGridApiContext } from '@mui/x-data-grid'
import { useLocations } from '../../../hooks/locations'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import {
  EmissionTier,
  MetricState,
  stateColorMapping,
  CanonicalPeriod,
  canonicalPeriodLabels,
  metricStateLabels,
  PERIODS,
  getPeriodYear,
} from '../common'
import { WildfireScoreBadge } from '../common/fireRisk/utils'
import { useHistory } from 'react-router-dom'
import { useCurrentUser } from '../../../hooks/users'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { LoadingValue } from './PortfolioLoading'

function compactNumber(n: number) {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)
}
function fixed1(n: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n)
}

function StatusChip({ value }: { value?: MetricState | null }) {
  if (!value) return <span>—</span>
  const label = metricStateLabels[value] || value
  const color = stateColorMapping[value]
  return (
    <span style={{ background: color, color: 'white', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>
      {label}
    </span>
  )
}

function NumberCell({
  value,
  fmt = 'compact',
  suffix,
  prefix = '',
  loading,
}: {
  value?: number | null
  fmt?: 'compact' | 'fixed1'
  suffix?: string
  prefix?: string
  loading?: boolean
}) {
  return (
    <LoadingValue loading={loading} width={100} fallback="—">
      {typeof value === 'number'
        ? suffix
          ? `${prefix}${fmt === 'compact' ? compactNumber(value) : fixed1(value)} ${suffix}`
          : `${prefix}${fmt === 'compact' ? compactNumber(value) : fixed1(value)}`
        : null}
    </LoadingValue>
  )
}

function PropertyFilterToolbar() {
  const apiRef = useGridApiContext()
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', px: 0, py: 1 }}>
      <Typography variant="subtitle1">Property Breakdown</Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltIcon />}
          onClick={() => apiRef.current?.showFilterPanel?.()}>
          Filter
        </Button>
      </Stack>
    </GridToolbarContainer>
  )
}

type Row = {
  id: number
  name: string
  country?: string
  score: Partial<Record<CanonicalPeriod, number | null>>
  aal: Partial<Record<CanonicalPeriod, number | null>>
  bi: Partial<Record<CanonicalPeriod, number | null>>
  statusScore?: MetricState
  statusAAL?: MetricState
  statusBI?: MetricState
}

export default function PortfolioWildfire({ emissionTier }: { emissionTier: EmissionTier }) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations, isLoading: locationsLoading } = useLocations({ group_id: groupId })
  const { data: scoresByProperty, isLoading: scoreLoading } = usePortfolioClimateData('wf:score', {
    groupId: groupId ?? undefined,
  })
  const { data: aalByProperty, isLoading: aalLoading } = usePortfolioClimateData('wf:damage_loss', {
    groupId: groupId ?? undefined,
  })
  const { data: biByProperty, isLoading: biLoading } = usePortfolioClimateData('wf:bi_days', {
    groupId: groupId ?? undefined,
  })
  const { currencySymbol } = useCurrentUser()

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'wf', emissionTier)),
    [emissionTier, history],
  )

  const rows: Row[] = useMemo(() => {
    const base: Record<number, Row> = {}
    locations.forEach((p: any) => {
      base[p.id] = {
        id: p.id,
        name: p.name || `Property ${p.id}`,
        country: p.address?.country,
        score: {},
        aal: {},
        bi: {},
      }
    })

    const tierKey = emissionTier as unknown as string // 'low' | 'moderate' | 'high'

    const pickY = (entry: any, period: CanonicalPeriod): number | null => {
      // data.data[tier][period] -> {x, y}
      if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
      const node = entry.data?.data?.[tierKey]?.[period]
      const y = node?.y
      return typeof y === 'number' ? y : null
    }

    if (scoresByProperty && typeof scoresByProperty === 'object') {
      Object.values(scoresByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || {
          id: entry.property_id,
          name: `Property ${entry.property_id}`,
          score: {},
          aal: {},
          bi: {},
        }
        r.statusScore = entry.state?.value
        PERIODS.forEach(p => (r.score[p] = pickY(entry, p)))
        base[entry.property_id] = r
      })
    }

    if (aalByProperty && typeof aalByProperty === 'object') {
      Object.values(aalByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || {
          id: entry.property_id,
          name: `Property ${entry.property_id}`,
          score: {},
          aal: {},
          bi: {},
        }
        r.statusAAL = entry.state?.value
        PERIODS.forEach(p => (r.aal[p] = pickY(entry, p)))
        base[entry.property_id] = r
      })
    }

    if (biByProperty && typeof biByProperty === 'object') {
      Object.values(biByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || {
          id: entry.property_id,
          name: `Property ${entry.property_id}`,
          score: {},
          aal: {},
          bi: {},
        }
        r.statusBI = entry.state?.value
        PERIODS.forEach(p => (r.bi[p] = pickY(entry, p)))
        base[entry.property_id] = r
      })
    }

    return Object.values(base)
  }, [locations, emissionTier, scoresByProperty, aalByProperty, biByProperty])

  // KPIs per period
  const kpis = useMemo(() => {
    type Agg = {
      avgScore: number | null
      totalAAL: number | null
      avgBI: number | null
      highCount: number // score >= 4
    }
    const out: Record<CanonicalPeriod, Agg> = {
      [CanonicalPeriod.SHORT_TERM]: { avgScore: null, totalAAL: null, avgBI: null, highCount: 0 },
      [CanonicalPeriod.MID_TERM]: { avgScore: null, totalAAL: null, avgBI: null, highCount: 0 },
      [CanonicalPeriod.LONG_TERM]: { avgScore: null, totalAAL: null, avgBI: null, highCount: 0 },
    }

    PERIODS.forEach(p => {
      const sVals = rows.map(r => r.score[p]).filter((v): v is number => typeof v === 'number')
      const aVals = rows.map(r => r.aal[p]).filter((v): v is number => typeof v === 'number')
      const bVals = rows.map(r => r.bi[p]).filter((v): v is number => typeof v === 'number')
      const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
      const sum = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) : null)
      out[p] = {
        avgScore: mean(sVals),
        totalAAL: sum(aVals),
        avgBI: mean(bVals),
        highCount: sVals.filter(v => v >= 4).length,
      }
    })

    return out
  }, [rows])

  const gridLoading = locationsLoading && locations.length === 0

  // Columns: single table showing all 3 metrics per property
  const columns: GridColDef<Row>[] = useMemo(
    () => [
      { field: 'name', headerName: 'Property', width: 220 },
      { field: 'country', headerName: 'Country', width: 120 },

      // Score columns per period
      ...PERIODS.map(
        (period): GridColDef<Row> => ({
          field: `score_${period}`,
          headerName: `Rating (${getPeriodYear(period)})`,
          width: 130,
          valueGetter: params => {
            const v = params.row.score[period]
            return typeof v === 'number' ? v : null
          },
          renderCell: params => (
            <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
              <WildfireScoreBadge score={(params.value as number | null) ?? null} size="dense" />
            </LoadingValue>
          ),
        }),
      ),

      // AAL columns per period
      ...PERIODS.map(
        (period): GridColDef<Row> => ({
          field: `aal_${period}`,
          headerName: `AAL (${getPeriodYear(period)})`,
          width: 150,
          valueGetter: params => {
            const v = params.row.aal[period]
            return typeof v === 'number' ? v : null
          },
          renderCell: params => (
            <NumberCell
              value={(params.value as number | null) ?? null}
              prefix={currencySymbol}
              suffix="/ year"
              loading={aalLoading}
            />
          ),
        }),
      ),

      // BI Days columns per period
      ...PERIODS.map(
        (period): GridColDef<Row> => ({
          field: `bi_${period}`,
          headerName: `Interruption (${getPeriodYear(period)})`,
          width: 150,
          valueGetter: params => {
            const v = params.row.bi[period]
            return typeof v === 'number' ? v : null
          },
          renderCell: params => (
            <NumberCell value={(params.value as number | null) ?? null} fmt="fixed1" suffix="days/year" loading={biLoading} />
          ),
        }),
      ),

      {
        field: 'statusScore',
        headerName: 'Status (Score)',
        width: 140,
        sortable: false,
        valueGetter: p => p.row.statusScore ?? null,
        renderCell: p => (
          <LoadingValue loading={scoreLoading} variant="rounded" width={80} height={18}>
            <StatusChip value={p.value as MetricState | null} />
          </LoadingValue>
        ),
      },
      {
        field: 'statusAAL',
        headerName: 'Status (AAL)',
        width: 140,
        sortable: false,
        valueGetter: p => p.row.statusAAL ?? null,
        renderCell: p => (
          <LoadingValue loading={aalLoading} variant="rounded" width={80} height={18}>
            <StatusChip value={p.value as MetricState | null} />
          </LoadingValue>
        ),
      },
      {
        field: 'statusBI',
        headerName: 'Status (BI)',
        width: 140,
        sortable: false,
        valueGetter: p => p.row.statusBI ?? null,
        renderCell: p => (
          <LoadingValue loading={biLoading} variant="rounded" width={80} height={18}>
            <StatusChip value={p.value as MetricState | null} />
          </LoadingValue>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <Button size="small" onClick={() => goToProperty(params.row.id)}>
            View property
          </Button>
        ),
      },
    ],
    [currencySymbol, goToProperty, scoreLoading, aalLoading, biLoading],
  )

  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {/* KPI strip */}
        <Grid container spacing={2}>
          {/* Average Score per period */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="overline">Average Risk Rating</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(p => (
                    <Stack key={p} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{canonicalPeriodLabels[p]}</Typography>
                      <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
                        <WildfireScoreBadge score={kpis[p].avgScore} size="dense" />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* High / Very High counts */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="overline"># of High / Very High Risk Ratings</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(p => (
                    <Stack key={p} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{canonicalPeriodLabels[p]}</Typography>
                      <LoadingValue loading={scoreLoading} variant="rounded" width={60} height={24}>
                        <Chip label={kpis[p].highCount} />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Total AAL per period */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="overline">Total Annual Loss (AAL)</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(p => (
                    <Stack key={p} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{canonicalPeriodLabels[p]}</Typography>
                      <LoadingValue loading={aalLoading} variant="rounded" width={140} height={24}>
                        <Chip
                          label={
                            kpis[p].totalAAL != null
                              ? `${currencySymbol}${compactNumber(kpis[p].totalAAL!)} / year`
                              : '—'
                          }
                        />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Average BI Days per period */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="overline">Average Interruption</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(p => (
                    <Stack key={p} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{canonicalPeriodLabels[p]}</Typography>
                      <LoadingValue loading={biLoading} variant="rounded" width={120} height={24}>
                        <Chip label={kpis[p].avgBI != null ? `${fixed1(kpis[p].avgBI!)} days/year` : '—'} />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Single table with all three metrics per property */}
        <Card>
          <CardContent>
            <div style={{ height: 680, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={r => r.id}
                loading={gridLoading}
                initialState={{
                  sorting: { sortModel: [{ field: `score_${CanonicalPeriod.LONG_TERM}`, sort: 'desc' }] },
                  columns: {
                    columnVisibilityModel: {
                      statusScore: false,
                      statusAAL: false,
                      statusBI: false,
                    },
                  },
                }}
                density="compact"
                onRowDoubleClick={params => goToProperty(params.row.id)}
                disableSelectionOnClick
                components={{ Toolbar: PropertyFilterToolbar }}
                sx={{
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover },
                }}
              />
            </div>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  )
}
